// /app/frontend/src/pages/InnerWorkPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Calendar, TrendingUp, Sparkles, Trash2, Share2, Users } from "lucide-react";
import { getTodayPrompt, listReflections, monthStats, deleteReflection } from "../api/innerWork";
import { listMatches } from "../api/matches";
import InnerWorkModal from "../components/InnerWorkModal";
import ShareReflectionModal from "../components/innerwork/ShareReflectionModal";
import ShareToRoomModal from "../components/innerwork/ShareToRoomModal";
import ShareDestinationModal from "../components/innerwork/ShareDestinationModal";

export default function InnerWorkPage() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [matches, setMatches] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareReflection, setShareReflection] = useState(null);
  const [roomShareOpen, setRoomShareOpen] = useState(false);
  const [roomShareReflection, setRoomShareReflection] = useState(null);
  const [sharePickerOpen, setSharePickerOpen] = useState(false);
  const [selectedReflection, setSelectedReflection] = useState(null);

  const now = useMemo(() => new Date(), []);
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, r, s, m] = await Promise.all([
        getTodayPrompt(),
        listReflections({ limit: 50 }),
        monthStats({ year, month }),
        listMatches().catch(() => []), // Graceful fallback if matches fail
      ]);
      setPrompt(p);
      setReflections(Array.isArray(r) ? r : []);
      setStats(s || null);
      setMatches(Array.isArray(m) ? m : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onCreated = () => refresh();

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this reflection? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await deleteReflection(id);
      setReflections((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not delete.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen surface-page text-slate-900 dark:text-white">
      <div className="mx-auto w-full max-w-4xl p-4 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-xl p-2 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold">Inner Work</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Private reflections for deeper connection</p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-xl bg-rose-500 hover:bg-rose-600 px-4 py-2 text-sm font-medium transition"
            onClick={() => setOpen(true)}
          >
            + New Reflection
          </button>
        </div>

        {/* Top Grid: Today's Topic + Stats */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-6">
          {/* Today's Topic */}
          <div className="md:col-span-2 rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-wider text-slate-500">Today&apos;s Reflection</span>
            </div>
            <div className="text-base text-slate-200">
              {loading ? "Loading…" : prompt?.prompt_text || "No topic scheduled today. Reflect on anything that's on your mind."}
            </div>
            <button
              type="button"
              className="mt-4 rounded-xl bg-purple-500 hover:bg-purple-600 px-5 py-2.5 text-sm font-medium text-white transition"
              onClick={() => setOpen(true)}
            >
              Reflect now
            </button>
          </div>

          {/* Monthly Stats */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span className="text-xs uppercase tracking-wider text-slate-500">This Month</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {stats?.reflections_count ?? (loading ? "…" : 0)}
            </div>
            <div className="text-xs text-slate-400 mt-1">Reflections saved</div>
            
            <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Top feeling</div>
                <div className="text-sm text-slate-200 mt-1">
                  {stats?.top_feeling_tag ? (
                    <span className="capitalize">{stats.top_feeling_tag}</span>
                  ) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Avg clarity</div>
                <div className="text-sm text-slate-200 mt-1 flex items-center gap-2">
                  {typeof stats?.avg_clarity_score === "number" ? (
                    <>
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                      {stats.avg_clarity_score.toFixed(1)} / 5
                    </>
                  ) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reflections List */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold">My Reflections</span>
              <span className="text-xs text-slate-500">• Only you can see these</span>
            </div>
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-800 transition"
              onClick={refresh}
            >
              Refresh
            </button>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
            ) : reflections.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                No reflections yet. Start with today&apos;s prompt above.
              </div>
            ) : (
              reflections.map((r) => (
                <div key={r.id} className="rounded-xl bg-slate-800/50 border border-slate-700/50 p-4 group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mb-2">
                        <span>{new Date(r.created_at).toLocaleDateString()}</span>
                        <span>{new Date(r.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {r.feeling_tag ? (
                          <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 capitalize">
                            {r.feeling_tag}
                          </span>
                        ) : null}
                        {r.clarity_score ? (
                          <span className="text-emerald-400">clarity: {r.clarity_score}/5</span>
                        ) : null}
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-slate-200">{r.reflection_text}</div>
                      
                      {/* Share button */}
                      <div className="mt-3">
                        <button
                          type="button"
                          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 transition font-medium"
                          onClick={() => {
                            setSelectedReflection(r);
                            setSharePickerOpen(true);
                          }}
                          data-testid={`share-reflection-${r.id}`}
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Share Reflection
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-slate-700 text-slate-500 hover:text-red-400 transition"
                      onClick={() => handleDelete(r.id)}
                      disabled={deleting === r.id}
                      title="Delete reflection"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <InnerWorkModal
        open={open}
        onClose={() => setOpen(false)}
        prompt={prompt}
        onCreated={onCreated}
        title="Inner Work — Reflection"
      />

      <ShareReflectionModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        reflection={shareReflection}
        matches={matches}
        onShared={() => {}}
      />

      <ShareToRoomModal
        open={roomShareOpen}
        onClose={() => setRoomShareOpen(false)}
        reflection={roomShareReflection}
        onShared={() => {}}
      />

      <ShareDestinationModal
        open={sharePickerOpen}
        onClose={() => setSharePickerOpen(false)}
        onSelect={(type) => {
          setSharePickerOpen(false);
          if (type === "chat") {
            setShareReflection(selectedReflection);
            setShareOpen(true);
          }
          if (type === "community") {
            setRoomShareReflection(selectedReflection);
            setRoomShareOpen(true);
          }
        }}
      />
    </div>
  );
}
